<script lang="ts">
import type {
  KaSelectEmits,
  KaSelectGroupProps,
  KaSelectOptionProps,
  KaSelectProps,
  KaSelectSelection,
  KaSelectValue,
} from './types';

export type {
  KaSelectEmits,
  KaSelectGroupProps,
  KaSelectOptionProps,
  KaSelectProps,
  KaSelectSelection,
  KaSelectValue,
} from './types';
</script>
<script
  setup
  lang="ts"
  generic="T extends KaSelectValue = KaSelectSelection, M extends boolean = false"
>
import { isEqual } from 'ohash/utils';
import { SelectContent, SelectPortal, SelectRoot, SelectTrigger, SelectViewport } from 'reka-ui';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  readonly,
  type Ref,
  ref,
  toRef,
  watch,
} from 'vue';

// Import each sub-component from its own entrypoint rather than the `@/kasten-components/components`
// barrel: the barrel pulls in the entire library (incl. chart components that depend on core-js's
// CJS Map polyfill), which fails to load in the Histoire sandbox ("require is not defined").
import { KaBadge } from '@/kasten-components/components/KaBadge';
import { KaDescription } from '@/kasten-components/components/KaDescription';
import { KaLabel } from '@/kasten-components/components/KaLabel';
import { KaMessage } from '@/kasten-components/components/KaMessage';
import { useDataTestid } from '@/kasten-components/composables';
import type { MaybeMultiple } from '@/kasten-components/types';
import { dom, unwrapElement } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';
import ChevronUpDownIcon from '~icons/lucide/chevrons-up-down';
import IconClose from '~icons/lucide/x';

import { provideSelectContext } from './context';
import KaSelectGroup from './KaSelectGroup.vue';
import KaSelectIconSlot from './KaSelectIconSlot.vue';
import KaSelectOption from './KaSelectOption.vue';
import {
  selectClearButtonVariants,
  selectContentVariants,
  selectSelectedTextVariants,
  selectTriggerVariants,
  selectViewportVariants,
} from './select-variants';

const props = withDefaults(defineProps<KaSelectProps<T, M>>(), {
  modelValue: undefined,
  description: undefined,
  // @ts-expect-error generic-linked default
  multiple: false,
  hideLabel: false,
  hideDescription: false,
  required: false,
  disabled: false,
  clearable: false,
  renderOptionsInPlace: false,
  hideBadges: false,
  autoFocus: false,
  autoFocusDelayMs: 0,
});

// Slot return types are `any` on purpose: Vue ignores the return type of a `defineSlots` entry
// (it only type-checks the slot props), and using `VNode | VNode[]` here makes vue-tsc recursively
// expand VNode's self-referential children type across every slot on this generic component until
// it trips TS2589 ("Type instantiation is excessively deep"). `any` is Vue's documented convention.
const slots = defineSlots<{
  label?(props: { label: string }): any;
  description?(props: { description?: string }): any;
  left?(props: { selected?: MaybeMultiple<T, M> | null }): any;
  right?(props: { selected?: MaybeMultiple<T, M> | null }): any;
  arrow?(props: Record<string, never>): any;
  optionItem?(props: { option: KaSelectOptionProps; index: number }): any;
}>();

const emit = defineEmits<KaSelectEmits<T, M>>();

// KaSelect renders a fragment (the tab sentinels, the trigger wrapper, and reka's hidden native
// <select>), so there's no single root for fallthrough attrs to inherit onto. inheritAttrs: false
// keeps consumer / KaVeeFormField attrs (e.g. vee-validate's handlers) in $attrs instead of leaking
// onto the trigger or firing Vue's "extraneous non-props attributes" warning. In KaCombobox the same
// directive is load-bearing — its text input would otherwise receive vee-validate's onInput and
// corrupt the field value — so keep them consistent.
defineOptions({
  inheritAttrs: false,
});

const name = computed(() => props.name);
const id = computed(() => props.id ?? name.value);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));
const labelId = computed(() => `${id.value}-label`);

const isRequired = computed(() => props.required || props.ariaRequired);
const hasError = computed(() => props.errorMessage !== undefined);
const isInvalid = computed(() => props.showErrorStylesOnly || hasError.value);

const shouldDisplayDescription = computed(
  // Multiple-select always renders a description so the trigger can announce the current selection
  // to assistive tech (the visual badges are not exposed as the combobox's value).
  () => props.description || slots.description || props.multiple,
);
const shouldDisplayDescriptionMargin = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);
const descriptionId = computed(() =>
  shouldDisplayDescription.value ? `${id.value}-description` : undefined,
);

const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});

const isOpen = ref(false);
const beforeSentinel = ref<HTMLElement | null>(null);
const afterSentinel = ref<HTMLElement | null>(null);
const triggerEl = ref<HTMLElement | null>(null);
const clearButton = ref<HTMLButtonElement | null>(null);
const selectContent = ref<HTMLElement | null>(null);
const internalValue = ref(props.modelValue) as Ref<MaybeMultiple<T, M> | null | undefined>;

const options = computed(() => props.options ?? []);

// An entry is a group (rather than a selectable option) when it has no `value` field. Mirrors
// KaCombobox so a grouped option list moves between the two components unchanged.
const isGroup = (option: KaSelectOptionProps | KaSelectGroupProps): option is KaSelectGroupProps =>
  !('value' in option);

// Stable v-for key derived from each entry's data identity (a group's label, an option's value) —
// never the whole object, whose `left`/`right` icons may be Vue VNodes that JSON.stringify can't
// serialize (circular structures throw) and that are needlessly expensive to stringify.
const optionKey = (option: KaSelectOptionProps | KaSelectGroupProps) =>
  isGroup(option) ? `group-${option.label}` : `option-${JSON.stringify(option.value)}`;

const multipleValues = computed(
  () => internalValue.value as KaSelectOptionProps[] | null | undefined,
);

// Labels of the currently-selected options, used for the sr-only selection announcement in
// multiple mode (the visual badges aren't exposed as the combobox's accessible value).
const selectedLabels = computed(() =>
  (multipleValues.value ?? []).map((option) => option.label).filter(Boolean),
);

// Count for the announcement — derived from the full selection, not `selectedLabels` (which drops
// options without a `label`), so it always matches the number of badges shown.
const selectedCount = computed(() => multipleValues.value?.length ?? 0);

const hasSelectedOptions = computed(() =>
  Array.isArray(internalValue.value) ? internalValue.value.length > 0 : internalValue.value != null,
);

const selectedSingle = computed(() =>
  props.multiple ? undefined : (internalValue.value as KaSelectOptionProps | null | undefined),
);

// Compare options by their `value` field so object options round-trip through reka's `by`.
const compare = (a?: KaSelectOptionProps, b?: KaSelectOptionProps) => {
  if (!a || !b) return false;
  if (a.value === undefined || b.value === undefined) return false;
  if (typeof a.value === 'string') return a.value === b.value;
  return isEqual(a.value, b.value);
};

// Whether two model values represent the SAME selection (compared by option `value`, not object
// identity). A consumer that re-derives the selected option from a list which re-renders (e.g. a
// polled list) hands us a brand-new object each time with an unchanged `value`; treating that as a
// real change would needlessly churn reka's value. Ordered compare for `multiple` — it never reports
// a genuine change as "same" (a reorder is treated as a change, which is safe, just not optimal).
const isSameSelection = (
  a?: MaybeMultiple<T, M> | null,
  b?: MaybeMultiple<T, M> | null,
): boolean => {
  if (props.multiple) {
    const left = (Array.isArray(a) ? a : []) as KaSelectOptionProps[];
    const right = (Array.isArray(b) ? b : []) as KaSelectOptionProps[];
    return left.length === right.length && left.every((option, i) => compare(option, right[i]));
  }
  if (a == null || b == null) return a == null && b == null;
  return compare(a as KaSelectOptionProps, b as KaSelectOptionProps);
};

const focus = async () => {
  dom(triggerEl)?.focus({ preventScroll: true });
  await nextTick();
};

const handleUpdateModelValue = async (value?: MaybeMultiple<T, M> | null) => {
  internalValue.value = value;
  emit('update:modelValue', value);
  emit('change', value);

  // Single select: reka closes the menu on selection, so pull focus back to the trigger to keep
  // keyboard users anchored. Multiple select: the menu stays open and focus must remain inside the
  // listbox — forcing focus to the trigger here closes/desyncs the open menu and stops it reopening
  // on the next click. (Deselecting via a badge / the clear button refocuses separately.)
  if (!props.multiple) await focus();
};

const handleOpen = async (open: boolean) => {
  if (props.disabled) return;
  isOpen.value = open;
  await nextTick();
};

const clearSelectedOption = async () => {
  if (props.disabled) return;
  const cleared = (props.multiple ? [] : undefined) as MaybeMultiple<T, M> | null | undefined;
  internalValue.value = cleared;
  emit('update:modelValue', cleared);
  emit('change', cleared);
  await focus();
};

const deselect = async (option: KaSelectOptionProps) => {
  if (!Array.isArray(internalValue.value)) return;
  const next = internalValue.value.filter(
    (selected) => !compare(selected as KaSelectOptionProps, option),
  ) as MaybeMultiple<T, M>;
  internalValue.value = next;
  emit('update:modelValue', next);
  emit('change', next);
  await focus();
};

const shouldDispatchBlur = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget ?? document.activeElement;
  if (nextTarget === null) return true;
  if (!(nextTarget instanceof HTMLElement)) return true;

  // Badge remove buttons live inside the trigger, so focus moving to one is covered by the
  // `triggerElement.contains(nextTarget)` check below — no special-casing needed.
  const triggerElement = unwrapElement(triggerEl);
  const clearButtonElement = unwrapElement(clearButton);
  const contentElement = unwrapElement(selectContent);

  const triggerIsNotFocused = !triggerElement || !triggerElement.contains(nextTarget);
  const clearButtonIsNotFocused = !clearButtonElement || !clearButtonElement.contains(nextTarget);
  const contentIsNotFocused = !contentElement || !contentElement.contains(nextTarget);

  return triggerIsNotFocused && clearButtonIsNotFocused && contentIsNotFocused;
};

const handleBlur = (event: FocusEvent) => {
  setTimeout(() => {
    if (shouldDispatchBlur(event)) {
      emit('blur', event);
      if (isOpen.value) isOpen.value = false;
    }
  });
};

// reka's SelectTrigger re-focuses the trigger in its own click handler (a Safari
// label-click workaround). On a real mouse open the popup has already moved focus
// onto the active option by the time `click` fires, so that re-focus steals it back
// and arrow keys do nothing until an option is hovered. When the listbox is open and
// focus already sits inside it, stop reka's click handler so focus stays on the option.
const handleTriggerClickCapture = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const content = unwrapElement(selectContent);
  if (content?.contains(document.activeElement)) event.stopImmediatePropagation();
};

// --- Tab-to-dismiss --------------------------------------------------------
// reka's SelectContent traps the Tab key (it calls preventDefault on Tab keydown) and refocuses the
// trigger when the listbox closes. That leaves a keyboard user stuck on the control: they must press
// Escape (or select) before they can tab to the next field. KaCombobox (built on reka's non-modal
// Combobox) doesn't have this problem — Tab naturally blurs the input and moves on. Mirror that here,
// which matches the WAI-ARIA select-only combobox pattern: Tab closes the popup and advances focus to
// the next/previous form control.
//
// `false` = not tabbing out; otherwise the direction the focus should move once the listbox unmounts.
const tabbingOut = ref<false | 'forward' | 'backward'>(false);

// Move focus to the tabbable element immediately before/after the widget. The sentinel spans placed
// just outside the widget act as DOM-position anchors: the walker starts at the relevant sentinel and
// takes a single step, rather than scanning the whole document on every tab-out. Because the sentinels
// sit outside the widget, stepping from one also naturally skips the widget's own tabbables (clear
// button, badge remove buttons). Falls back to the trigger at the edge of the tab order so focus is
// never dropped to <body>.
const focusSiblingOfWidget = (direction: 'forward' | 'backward') => {
  const sentinel = direction === 'forward' ? afterSentinel.value : beforeSentinel.value;
  if (!sentinel) {
    focus();
    return;
  }

  // Approximate tabbable filter (ignores visibility) — fine here since we only locate the widget's
  // immediate neighbour in document tab order; the tabindex="-1" sentinels themselves are skipped.
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      const el = node as HTMLElement;
      const isHiddenInput = el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'hidden';
      if ((el as HTMLButtonElement).disabled || el.hidden || isHiddenInput) {
        return NodeFilter.FILTER_SKIP;
      }
      return el.tabIndex >= 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });

  walker.currentNode = sentinel;
  const target =
    direction === 'forward'
      ? (walker.nextNode() as HTMLElement | null)
      : (walker.previousNode() as HTMLElement | null);

  if (target) target.focus({ preventScroll: true });
  else focus();
};

const handleContentKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Tab') return;
  // reka preventDefaults Tab itself; flag the direction and close the listbox. The actual focus move
  // is deferred to reka's close-autofocus (see handleCloseAutoFocus) so it happens after the listbox
  // content unmounts and `aria-hidden` is lifted from the rest of the app.
  event.preventDefault();
  tabbingOut.value = event.shiftKey ? 'backward' : 'forward';
  isOpen.value = false;
};

const handleCloseAutoFocus = (event: Event) => {
  if (!tabbingOut.value) return;
  // Suppress reka's "return focus to the trigger" so we can send focus onward instead. This event
  // fires as the listbox content unmounts (FocusScope teardown). `aria-hidden` is removed by a
  // sibling unmount hook that runs just after, so defer one frame: focusing the next control before
  // that would land on an element the rest of the app still has aria-hidden on (silent to AT).
  event.preventDefault();
  const direction = tabbingOut.value;
  tabbingOut.value = false;
  requestAnimationFrame(() => focusSiblingOfWidget(direction));
};

watch(
  () => isOpen.value,
  (newIsOpen) => {
    if (newIsOpen) {
      emit('touch');
      emit('open');
    } else {
      emit('close');
    }
  },
);

watch(
  () => props.modelValue,
  (newValue) => {
    // Keep the value stable across re-renders that don't actually change the selection. Without this,
    // a consumer feeding a freshly-derived selected option each poll would re-assign internalValue
    // (and therefore reka's modelValue) on every poll even though the chosen value is unchanged.
    if (isSameSelection(newValue, internalValue.value)) return;
    internalValue.value = newValue;
  },
);

// Provide the computed refs directly (not `toRef(value)`, which would snapshot the current string
// and stop tracking changes) so a consumer's injected `dataTestid`/`id` stay reactive.
provideSelectContext({
  dataTestid: readonly(dataTestid),
  id: readonly(id),
});

// reka's SelectTrigger writes `aria-controls` from a non-reactive content id and keeps it after
// <Presence> unmounts the listbox on close, leaving a stale id that fails axe `aria-valid-attr-value`.
// Sync it against the live content element: set it while mounted, strip it otherwise.
watch(
  [isOpen, selectContent],
  async () => {
    await nextTick();
    const trigger = unwrapElement(triggerEl);
    if (!trigger) return;
    const content = unwrapElement(selectContent);
    if (isOpen.value && content?.id) {
      if (trigger.getAttribute('aria-controls') !== content.id) {
        trigger.setAttribute('aria-controls', content.id);
      }
    } else {
      trigger.removeAttribute('aria-controls');
    }
  },
  { flush: 'post', immediate: true },
);

let autoFocusTimeoutId: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
  if (props.autoFocus) {
    autoFocusTimeoutId = setTimeout(() => {
      focus();
    }, props.autoFocusDelayMs);
  }
});

// Clear the pending autoFocus timer if the component unmounts before it fires, so focus() never runs
// against a torn-down trigger and we don't leave a dangling timer.
onBeforeUnmount(() => {
  if (autoFocusTimeoutId !== undefined) clearTimeout(autoFocusTimeoutId);
});

defineExpose({
  triggerRef: toRef(() => dom(triggerEl)),
  focus,
});
</script>
<template>
  <SelectRoot
    :model-value="internalValue"
    :open="isOpen"
    :name="name"
    :disabled="props.disabled"
    :required="isRequired"
    :by="(a, b) => compare(a as KaSelectOptionProps, b as KaSelectOptionProps)"
    :multiple="props.multiple"
    @update:model-value="
      (value: any) => handleUpdateModelValue(value as MaybeMultiple<T, M> | null)
    "
    @update:open="handleOpen"
  >
    <!--
      SelectRoot renders no DOM wrapper of its own (unlike ComboboxRoot), so we provide one to group
      the label, trigger, error message, etc. This wrapper MUST contain everything (including the
      sentinels) so KaSelect contributes a single layout box to its parent — otherwise, in a flex or
      grid container, each top-level node becomes its own flex/grid item (e.g. the leading sentinel
      would claim a whole `1fr` grid column, leaving a large empty space before the control).
      The sentinel spans flank the trigger in DOM order as position anchors for Tab-to-dismiss
      (see focusSiblingOfWidget): tabindex="-1" keeps them out of the tab order and aria-hidden hides
      them from assistive tech. Their next/previous focusable sibling is unchanged by the nesting.
    -->
    <div class="w-full">
      <span ref="beforeSentinel" tabindex="-1" aria-hidden="true" />
      <KaLabel
        :id="labelId"
        :data-testid="suffix('label')"
        :hide-label="props.hideLabel"
        :class="!shouldDisplayDescriptionMargin ? 'mx-0 mb-3 mt-0' : 'm-0'"
        :required="isRequired"
        :hide-required-asterisk="props.hideRequiredAsterisk"
      >
        <slot name="label" v-bind="{ label: props.label }">{{ props.label }}</slot>
      </KaLabel>

      <KaDescription
        v-if="shouldDisplayDescription"
        :id="descriptionId"
        :data-testid="suffix('description')"
        :hide-description="props.hideDescription"
        :class="shouldDisplayDescriptionMargin ? 'mx-0 mb-3 mt-0' : 'm-0'"
      >
        <slot name="description" v-bind="{ description: props.description }">
          <span>{{ props.description }}</span>
        </slot>

        <!--
          Announce the current selection to assistive tech. The visual badges are not exposed as the
          combobox's value, so without this a screen reader on the (closed) trigger would not say
          what is selected. Rendered for all multiple-select, not just hideBadges.
        -->
        <template v-if="props.multiple">
          <span v-if="selectedCount" class="sr-only"
            >{{ selectedCount }} options selected{{
              selectedLabels.length ? `: ${selectedLabels.join(', ')}` : ''
            }}.</span
          >
          <span v-else class="sr-only">There are no options selected.</span>
        </template>
      </KaDescription>

      <!--
      Option A trigger: the shell is rendered as a <div> (via `as`), so the clear button and
      multi-select badge remove buttons are valid siblings/descendants and never nested inside a
      <button>. reka still wires role="combobox", aria-expanded and keydown/pointerdown handling
      onto this element and anchors the popper to it, so the whole box is the combobox control.
      A <div> is not focusable on its own, so we add tabindex ourselves (removed when disabled).
    -->
      <SelectTrigger
        :id="id"
        ref="triggerEl"
        as="div"
        :disabled="props.disabled"
        :tabindex="props.disabled ? -1 : 0"
        :data-testid="dataTestid"
        :aria-labelledby="labelId"
        :aria-describedby="descriptionId"
        :aria-required="isRequired"
        :aria-invalid="isInvalid"
        :aria-errormessage="computedErrorMessageId"
        :aria-disabled="props.disabled || undefined"
        :class="
          cnMerge(
            selectTriggerVariants({ invalid: isInvalid, disabled: props.disabled, open: isOpen }),
            props.anchorClasses,
            props.triggerClasses,
          )
        "
        @click.capture="handleTriggerClickCapture"
        @blur="handleBlur"
      >
        <div class="flex w-[calc(100%-52px)] flex-1 items-center justify-between">
          <KaSelectIconSlot
            v-if="'left' in slots || props.left"
            :data-testid="suffix('left')"
            :icon-options="props.left"
            class="mr-2 size-6 flex-shrink-0 flex-grow-0 text-current"
          >
            <slot name="left" :selected="internalValue" />
          </KaSelectIconSlot>

          <!-- Multiple selection: render selected options as badges -->
          <div
            v-if="props.multiple && !props.hideBadges"
            :data-testid="suffix('selected')"
            class="flex w-full flex-wrap gap-1"
          >
            <KaBadge
              v-for="option in multipleValues"
              :key="`${JSON.stringify(option.value)}-selected-select-badge`"
              :data-testid="suffix(`selected-${option.label}`)"
              :color="props.selectedBadgeColor"
              :left="option.left"
              :right="option.right"
              removeable
              class="max-w-full"
              @pointerdown.stop
              @remove:click.stop="() => deselect(option)"
              @remove:space.stop="() => deselect(option)"
              @remove:enter.stop="() => deselect(option)"
              @remove:blur="handleBlur"
            >
              <span class="truncate">{{ option.label }}</span>
            </KaBadge>

            <span v-if="!hasSelectedOptions" class="truncate text-gray-600 dark:text-gray-400">
              {{ props.placeholder }}
            </span>
          </div>

          <!-- Single selection: render the selected label or the placeholder -->
          <span
            v-else
            :data-testid="suffix('selected')"
            :title="selectedSingle?.label"
            :class="
              selectSelectedTextVariants({
                tone: isInvalid ? 'invalid' : hasSelectedOptions ? 'selected' : 'placeholder',
              })
            "
          >
            <template v-if="hasSelectedOptions">{{ selectedSingle?.label }}</template>
            <template v-else>{{ props.placeholder }}</template>
          </span>

          <KaSelectIconSlot
            v-if="'right' in slots || props.right"
            :data-testid="suffix('right')"
            :icon-options="props.right"
            class="ml-2 inline-flex flex-shrink-0 flex-grow-0 text-current"
          >
            <slot name="right" :selected="internalValue" />
          </KaSelectIconSlot>
        </div>

        <div class="flex flex-shrink-0 flex-grow-0 items-center gap-x-2">
          <button
            v-if="props.clearable && !props.disabled && hasSelectedOptions"
            ref="clearButton"
            :data-testid="suffix('clear-button')"
            type="button"
            :class="cnMerge(selectClearButtonVariants(), props.clearButtonClasses)"
            title="Clear selected"
            :aria-label="props.clearButtonLabel || `Clear selected ${props.label}`"
            @pointerdown.stop
            @click.prevent.stop="clearSelectedOption"
            @keydown.enter.stop.prevent="clearSelectedOption"
            @keydown.space.stop.prevent="clearSelectedOption"
            @blur="handleBlur"
          >
            <IconClose class="size-4" aria-hidden="true" />
          </button>

          <KaSelectIconSlot
            v-if="!props.hideArrow"
            :data-testid="suffix('arrow')"
            :icon-options="props.arrow"
            class="size-4 text-gray-600 outline-none dark:text-gray-500"
          >
            <slot v-if="!props.arrow" name="arrow">
              <ChevronUpDownIcon class="size-4" />
            </slot>
          </KaSelectIconSlot>
        </div>
      </SelectTrigger>

      <SelectPortal
        :disabled="props.renderOptionsInPlace || props.teleportProps?.disabled"
        :to="props.teleportProps?.to ?? 'body'"
        :defer="props.teleportProps?.defer"
      >
        <SelectContent
          ref="selectContent"
          :aria-labelledby="labelId"
          :aria-multiselectable="props.multiple || undefined"
          @keydown="handleContentKeydown"
          @close-auto-focus="handleCloseAutoFocus"
          :position="props.selectContentProps?.position ?? 'popper'"
          :body-lock="props.selectContentProps?.bodyLock ?? false"
          :align="props.selectContentProps?.align ?? 'start'"
          :avoid-collisions="props.selectContentProps?.avoidCollisions ?? true"
          :collision-padding="props.selectContentProps?.collisionPadding ?? 10"
          :class="
            cnMerge(
              selectContentVariants(),
              props.optionsZIndexClass || 'z-20',
              props.optionsContainerClasses,
            )
          "
        >
          <SelectViewport
            :data-testid="suffix('viewport')"
            :style="{ 'scrollbar-width': 'auto' }"
            :class="cnMerge(selectViewportVariants(), props.viewportClasses)"
          >
            <div
              v-if="options.length === 0"
              :data-testid="suffix('empty')"
              class="select-none px-4 py-2"
            >
              Sorry, no options available.
            </div>

            <template v-for="(option, index) in options" v-else :key="optionKey(option)">
              <KaSelectGroup v-if="isGroup(option)" v-bind="option" />

              <template v-else>
                <slot
                  v-if="'optionItem' in slots"
                  name="optionItem"
                  :option="option"
                  :index="index"
                />
                <KaSelectOption v-else v-bind="option" />
              </template>
            </template>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>

      <KaMessage
        v-if="!props.showErrorStylesOnly && hasError"
        :id="fallbackErrorMessageId"
        :data-testid="suffix('error-message')"
        :message="props.errorMessage"
        background="transparent"
        variant="error"
        :role="null"
        :class="['mt-2', { 'sr-only': props.hideErrorMessage }]"
      />
      <span ref="afterSentinel" tabindex="-1" aria-hidden="true" />
    </div>
  </SelectRoot>
</template>
<style>
@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes scale-out {
  from {
    opacity: 1;
    filter: blur(0px);
    transform: scale(1);
  }
  to {
    opacity: 0;
    filter: blur(20px);
    transform: scale(0.95);
  }
}
</style>
